import { useCallback, useMemo } from "react";

import type { ButtonProps } from "@braintrust/braintrust-ui-components";
import {
  Box,
  Button,
  Loader,
  Tooltip,
} from "@braintrust/braintrust-ui-components";
import { useIsNodeOrImpersonated } from "@js/apps/common/hooks/use-is-node-or-impersonated";
import type { EmployerBidListBid, Job } from "@js/types/jobs";

import { isAIInterviewButtonDisabled } from "../../helpers";
import { useBulkSelectActions } from "../../hooks/use-bulk-select-actions";

import { BidSelectionCheckbox } from "./bid-selection-checkbox";

const DEFAULT_DISABLED_ACTION_TOOLTIP_TEXT =
  "This action can't be performed on any of the selected applications.";

type BulkSelectActionsProps = {
  job: Job;
  bidList: EmployerBidListBid[];
  isShowingHiddenBids: boolean;
};

export const BulkSelectActions = ({
  job,
  bidList,
  isShowingHiddenBids,
}: BulkSelectActionsProps) => {
  const {
    bulkHideBids,
    bulkSendInterviewRequest,
    bulkSendAIInterviewRequest,
    bulkRejectOffer,
    bulkSendSms,
    canSendSms,
    bulkAddToRoster,
    canAddToRoster,
    handleShareBidsClick,
    handleSendSelectedApplications,
    selectedBidsTotal,
    selectedInterviewableBidsTotal,
    isAnyOfSelectedInterviewableBidsUnapproved,
    canSendInterviewRequest,
    canSendRejection,
    areAllSelectedBidsHidden,
    canShareBidsWithTeamMembers,
    canSendApplicationData,
    isSendingApplicationsData,
    isSendingApplicationsBlocked,
  } = useBulkSelectActions(job, bidList);
  const isNodeOrImpersonated = useIsNodeOrImpersonated();

  const isGrantJob = job.job_type === ENUMS.JobType.GRANT;
  const shouldRender = selectedBidsTotal > 0;
  const shouldShowUnhideAction = useMemo(
    () => isShowingHiddenBids || areAllSelectedBidsHidden,
    [areAllSelectedBidsHidden, isShowingHiddenBids],
  );

  const getSendInterviewRequestTitle = useCallback(() => {
    if (!canSendInterviewRequest) {
      return DEFAULT_DISABLED_ACTION_TOOLTIP_TEXT;
    }

    if (!isGrantJob && isAnyOfSelectedInterviewableBidsUnapproved) {
      return "One or more of the selected Talent are unapproved. Please de-select or approve Talent to proceed.";
    }

    const uninterviewableBidsTotal =
      selectedBidsTotal - selectedInterviewableBidsTotal;

    if (uninterviewableBidsTotal > 0) {
      return `${uninterviewableBidsTotal} of the selected Talent can't be sent an interview request (already past this stage, or not yet verified). You can proceed and the rest will still be invited.`;
    }
  }, [
    canSendInterviewRequest,
    isGrantJob,
    isAnyOfSelectedInterviewableBidsUnapproved,
    selectedBidsTotal,
    selectedInterviewableBidsTotal,
  ]);

  const sendApplicationsDataTitle = useMemo(() => {
    if (!canSendApplicationData) {
      return;
    }
    if (isSendingApplicationsBlocked) {
      return `All of the selected applications have already been sent to ${job.integration_label}.`;
    }
  }, [
    canSendApplicationData,
    isSendingApplicationsBlocked,
    job.integration_label,
  ]);

  if (!shouldRender) {
    return null;
  }

  const sendRejectionTitle = !canSendRejection
    ? DEFAULT_DISABLED_ACTION_TOOLTIP_TEXT
    : undefined;

  const sendInterviewRequestTitle = getSendInterviewRequestTitle();

  const isAIInterviewEnabled = !!job.is_ai_interview_enabled;
  const isInterviewRequestButtonDisabled =
    !canSendInterviewRequest ||
    (!isGrantJob && isAnyOfSelectedInterviewableBidsUnapproved);

  const isAIInterviewButtonDisabledCommon = isAIInterviewButtonDisabled(job);
  const isAIInterviewRequestButtonDisabledForEmployer =
    isInterviewRequestButtonDisabled || isAIInterviewButtonDisabledCommon;

  const isAIInterviewRequestButtonDisabledForNodeOrImpersonated =
    !canSendInterviewRequest || isAIInterviewButtonDisabledCommon;

  const isAIInterviewRequestButtonDisabled = isNodeOrImpersonated
    ? isAIInterviewRequestButtonDisabledForNodeOrImpersonated
    : isAIInterviewRequestButtonDisabledForEmployer;

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        rowGap: 2.5,
      }}
    >
      <BidSelectionCheckbox bidList={bidList} />
      <Box
        sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}
      >
        {!isAIInterviewEnabled && (
          <ActionButton
            onClick={bulkSendInterviewRequest}
            title={sendInterviewRequestTitle}
            disabled={isInterviewRequestButtonDisabled}
          >
            Send interview request
          </ActionButton>
        )}
        {isAIInterviewEnabled && (
          <ActionButton
            onClick={bulkSendAIInterviewRequest}
            title={sendInterviewRequestTitle}
            disabled={isAIInterviewRequestButtonDisabled}
          >
            Send AI interview request
          </ActionButton>
        )}

        <ActionButton
          onClick={bulkRejectOffer}
          title={sendRejectionTitle}
          disabled={!canSendRejection}
        >
          Send rejection
        </ActionButton>

        {canSendSms && (
          <ActionButton onClick={bulkSendSms}>Send SMS</ActionButton>
        )}

        {canAddToRoster && (
          <ActionButton onClick={bulkAddToRoster}>Add to roster</ActionButton>
        )}

        <ActionButton onClick={() => bulkHideBids(shouldShowUnhideAction)}>
          {shouldShowUnhideAction ? "Unhide" : "Hide 👎"}
        </ActionButton>

        {canShareBidsWithTeamMembers && (
          <ActionButton onClick={handleShareBidsClick}>Share</ActionButton>
        )}

        {canSendApplicationData && (
          <ActionButton
            onClick={handleSendSelectedApplications}
            title={sendApplicationsDataTitle}
            disabled={isSendingApplicationsBlocked}
            isLoading={isSendingApplicationsData}
          >
            Send to {job.integration_label}
          </ActionButton>
        )}
      </Box>
    </Box>
  );
};

type ActionButtonProps = Omit<
  ButtonProps,
  "variant" | "shape" | "size" | "href" | "to"
> & { title?: string; isLoading?: boolean };

const ActionButton = ({
  children,
  title,
  isLoading,
  ...rest
}: ActionButtonProps) => {
  const buttonProps: ButtonProps = {
    variant: "white-border-beige",
    shape: "squared",
    size: "medium",
    ...rest,
  };

  const button = (
    <Button
      {...buttonProps}
      endIcon={isLoading ? <Loader size={20} /> : undefined}
    >
      {children}
    </Button>
  );

  if (title) {
    return (
      <Tooltip title={title}>
        <span>{button}</span>
      </Tooltip>
    );
  }
  return button;
};
